
import { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { FlatTourData } from '@/utils/tourTransformUtils';
import { fetchTourDetails, fetchTourExhibits } from '@/services/tour/fetchTourDetails';
import { transformTourDataForDetail, transformTourExhibitsData } from '@/utils/tourDetailTransformUtils';
import { toast } from '@/hooks/use-toast';

export interface TourDetailExhibit {
  id: string;
  numberCode: string;
  titleEn: string;
  titleHe: string;
  image: string;
  location: {
    floor: number;
    room: string;
  };
}

export interface TourDetailData {
  tour: FlatTourData | null;
  tourExhibits: TourDetailExhibit[];
  loading: boolean;
  error: Error | null;
}

export function useTourDetail(id: string | undefined) {
  const { language } = useLanguage();
  const [tourData, setTourData] = useState<TourDetailData>({
    tour: null,
    tourExhibits: [],
    loading: true,
    error: null
  });
  
  useEffect(() => {
    if (!id) {
      setTourData(prev => ({
        ...prev,
        loading: false,
        error: new Error('No tour ID provided')
      }));
      return;
    }
    
    const loadTourDetails = async () => {
      try {
        console.log(`Starting to fetch tour details for ID: ${id}`);
        setTourData(prev => ({...prev, loading: true, error: null}));
        
        // Fetch tour and exhibit data
        const [tourDetails, tourExhibitsData] = await Promise.all([
          fetchTourDetails(id),
          fetchTourExhibits(id)
        ]);
        
        console.log('Tour details fetched successfully:', tourDetails?.id);
        console.log('Tour exhibits fetched:', tourExhibitsData?.length || 0);
        
        if (!tourDetails) {
          throw new Error(`Tour with ID ${id} not found`);
        }
        
        // Process exhibit data to get exhibit IDs
        const exhibitIds = Array.isArray(tourExhibitsData) ? 
          tourExhibitsData
            .filter(item => item?.exhibit?.id && item?.exhibit?.status === 'published')
            .map(item => item.exhibit.id) :
          [];
        
        console.log(`Found ${exhibitIds.length} published exhibits for tour ${id}`);
        
        // Transform the data
        const formattedTour = transformTourDataForDetail(tourDetails, language);
        const formattedExhibits = transformTourExhibitsData(tourExhibitsData || [], language);
        
        console.log('Transformed tour:', formattedTour);
        console.log('Transformed exhibits:', formattedExhibits?.length || 0);
        
        // Add exhibit IDs to tour data
        if (formattedTour) {
          formattedTour.exhibits = exhibitIds;
          formattedTour.exhibitCount = exhibitIds.length;
        }
        
        setTourData({
          tour: formattedTour,
          tourExhibits: formattedExhibits || [],
          loading: false,
          error: null
        });
      } catch (error) {
        console.error('Error fetching tour details:', error);
        toast({
          title: "Error loading tour",
          description: error instanceof Error ? error.message : "Failed to load tour details",
          variant: "destructive"
        });
        setTourData(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error : new Error('Unknown error')
        }));
      }
    };
    
    loadTourDetails();
  }, [id, language]);
  
  return tourData;
}
